import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    motion,
    useMotionValueEvent,
    useScroll,
    useTransform
} from 'framer-motion';

import useAnimate, { entry } from '../ui/Motion';

import WordReveal from '../ui/WordReveal';
import { site, contact, disciplines, seam } from '../data/site';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────────
   The hero is exactly one screen tall and is NOT pinned.

   A pin is the only mechanism on a page that can hold the viewport
   still while the scrollbar moves, so it is the only thing that can
   produce inert scroll. Without one, `offset: ['start start', 'end
   start']` maps p 0 → 1 across exactly the hero's own departure and
   every pixel of scroll necessarily reveals new material.

   Beat map (p is the fraction of the hero that has left the top):

     .00 → .10   scroll cue dismissed
     .03 → .40   three accent segments DRAW IN beneath the chips,
                 staggered: the first thing scrolling does is add
                 something, not subtract it
     .05 → .32   notice (if any), mark + headline dissolve as they
                 cross the top
     .12 → .44   lead paragraph
     .20 → .56   call-to-action buttons
     .32 → .70   the discipline chips, staggered: they outlive the
                 headline and are the last copy on screen
     .44 → .72   one neutral hairline resolves over the three accent
                 segments, and the next thing to enter the viewport
                 is the trade ticker's own hairline border
     .00 → 1.0   backdrop lags (+7vh) while the content leads (−9vh);
                 16vh of plane separation over the departure

   Amplitudes are viewport-relative (vh), not percentages of the
   content block, so the parallax reads the same at 1920×1080 and
   390×844: the block itself is ~2× taller on a phone.
   ───────────────────────────────────────────────────────────────── */

const T = {
    cueOut: 0.10,
    introOut: [0.05, 0.32],
    leadOut: [0.12, 0.44],
    actionsOut: [0.20, 0.56],
    chipIn: 0.32,
    chipOut: 0.70,
    unify: [0.44, 0.72],
    seam: [0.03, 0.30],
    /* Once a group is fully transparent it must stop being clickable and
       focusable, or an invisible phone-number link sits in the middle of
       the screen. */
    introSpent: 0.34,
    actionsSpent: 0.58,
    chipsSpent: 0.74,
    /* The aurora is a full-viewport `filter: blur(70px)` wrapping three
       children on infinite transform keyframes, so the browser
       re-rasterises a blurred full-screen surface every frame, for the
       whole session, not just the hero. Park it once the hero is behind
       the reader, and with it the background video, which is the single
       most expensive thing on the page to keep decoding unseen. */
    settle: 0.55
};

/* Hooks cannot run inside .map(), so each repeated element owns its own. */

function Chip({ item, index, progress, live }) {
    const opacity = useTransform(
        progress,
        [T.chipIn + index * 0.03, T.chipOut + index * 0.03],
        [1, 0]
    );

    return (
        <motion.li style={live ? { opacity } : undefined}>
            {/* The entry animation lives on the <a> and the scroll opacity on
                the <li>: two owners of one `opacity` silently fight, and the
                motion value in `style` always wins. */}
            <motion.div
                {...entry(live, { opacity: 0, y: 14 })}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.25 + index * 0.09, ease: EASE }}
            >
                <Link to={item.href}>
                    {item.Icon
                        ? <item.Icon className="hero__chip-icon" aria-hidden="true" />
                        : <span className="hero__chip-dot" />}
                    {item.label}
                </Link>
            </motion.div>
        </motion.li>
    );
}

function SeamBar({ colour, index, progress, live }) {
    const scaleX = useTransform(
        progress,
        [T.seam[0] + index * 0.05, T.seam[1] + index * 0.05],
        [0, 1]
    );

    const style = { '--seam': colour };

    return (
        <motion.span
            className="hero__seam-bar"
            style={live ? { ...style, scaleX } : style}
        />
    );
}

export default function Hero({ video, poster, title, lead, notice }) {
    const ref = useRef(null);
    const videoRef = useRef(null);
    const reduced = !useAnimate();
    const live = !reduced;

    /* One screen tall and unpinned, so p = 0 at rest and p = 1 the instant
       the hero's bottom edge reaches the top of the viewport. The timeline
       cannot outrun the section. */
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ['start start', 'end start']
    });

    /* NEAR: leads the page. Raw progress, so it stays under the finger. */
    const contentY = useTransform(scrollYProgress, [0, 0.8], ['0vh', '-9vh']);

    /* Each group fades as it crosses the top edge, rather than the whole
       block dimming while it sits squarely in the middle of the screen. */
    const introOpacity = useTransform(scrollYProgress, T.introOut, [1, 0]);
    const leadOpacity = useTransform(scrollYProgress, T.leadOut, [1, 0]);
    const actionsOpacity = useTransform(scrollYProgress, T.actionsOut, [1, 0]);

    /* Three accents resolve into one hairline, a cross-dissolve, so the
       colour genuinely leaves rather than tinting the line that replaces
       it. Two opacity writes; nothing repaints. */
    const barsOpacity = useTransform(scrollYProgress, T.unify, [1, 0]);
    const unifyOpacity = useTransform(scrollYProgress, T.unify, [0, 1]);

    /* FAR: lags the page, and keeps going after everything else has left,
       because its lower band is the only hero surface still on screen. */
    const backdropY = useTransform(scrollYProgress, [0, 1], ['0vh', '7vh']);
    const backdropScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.14]);

    const cueOpacity = useTransform(scrollYProgress, [0, T.cueOut], [1, 0]);

    /* Four booleans for the whole departure. Each flips once; React bails
       out of the render when nothing changed. */
    const [phase, setPhase] = useState({
        introSpent: false,
        actionsSpent: false,
        chipsSpent: false,
        settled: false
    });

    const applyPhase = value => {
        const next = {
            introSpent: value > T.introSpent,
            actionsSpent: value > T.actionsSpent,
            chipsSpent: value > T.chipsSpent,
            settled: value > T.settle
        };

        setPhase(prev =>
            prev.introSpent === next.introSpent &&
            prev.actionsSpent === next.actionsSpent &&
            prev.chipsSpent === next.chipsSpent &&
            prev.settled === next.settled
                ? prev
                : next
        );
    };

    useMotionValueEvent(scrollYProgress, 'change', value => {
        if (live) applyPhase(value);
    });

    /* Scroll restoration and deep links can land mid-page with no change
       event ever firing. */
    useEffect(() => {
        if (live) applyPhase(scrollYProgress.get());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [live]);

    /* Decoding 1080p H.264 that nobody can see costs a continuous slice of
       the main thread and, on a laptop, of the battery. Pause it once the
       hero has left, resume when the reader comes back. */
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;

        if (phase.settled) {
            el.pause();
            return;
        }

        /* HTMLMediaElement.play() returns a promise in every browser, but
           not in jsdom and not in older WebViews, so the rejection handler
           has to be attached defensively. An autoplay-policy rejection is
           expected and harmless: the poster frame stands in. */
        const played = el.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
    }, [phase.settled]);

    const introGone = live && phase.introSpent;
    const actionsGone = live && phase.actionsSpent;
    const chipsGone = live && phase.chipsSpent;

    return (
        <section className={`hero${notice ? ' hero--notice' : ''}`} id="top" ref={ref}>
            <motion.div
                className="hero__backdrop"
                aria-hidden="true"
                style={live ? { y: backdropY, scale: backdropScale } : undefined}
            >
                {/* The poster is the <video>'s own attribute, not a second
                    element. As a sibling <img> it was absolutely positioned
                    while the video was in normal flow, and a positioned box
                    paints above a non-positioned one whatever the DOM order
                    says, so the still sat over the footage at opacity 0.5 for
                    the life of the page. The attribute does the same job and
                    the browser swaps it out the moment the first frame is
                    ready. */}
                {video && (
                    <video
                        ref={videoRef}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        poster={poster}
                    >
                        <source src={video} type="video/mp4" />
                    </video>
                )}

                {!video && poster && <img src={poster} alt="" />}
            </motion.div>

            <div
                className="hero__aurora"
                data-settled={phase.settled ? 'true' : 'false'}
                aria-hidden="true"
            >
                <span className="hero__blob hero__blob--lead" />
                <span className="hero__blob hero__blob--b" />
                <span className="hero__blob hero__blob--c" />
            </div>

            <div className="hero__vignette" aria-hidden="true" />

            <motion.div
                className="hero__content"
                style={live ? { y: contentY } : undefined}
            >
                <motion.div
                    className="hero__intro"
                    style={live ? { opacity: introOpacity } : undefined}
                >
                    {/* One line of news, above everything it is news about.
                        It arrives last, after the chips, so the eye is drawn
                        back up to it once the composition has settled, and it
                        leaves with the headline as the two cross the top. */}
                    {notice && (
                        <motion.div
                            {...entry(live, { opacity: 0, y: -12 })}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, delay: 1.7, ease: EASE }}
                        >
                            <Link
                                className="hero__notice"
                                to={notice.href}
                                inert={introGone ? '' : undefined}
                                aria-hidden={introGone ? 'true' : undefined}
                            >
                                <span className="hero__notice-tag">{notice.tag}</span>
                                <span className="hero__notice-text">{notice.text}</span>
                                <span className="hero__notice-arrow" aria-hidden="true">→</span>
                            </Link>
                        </motion.div>
                    )}

                    {/* The float lives on a wrapper: a CSS animation on
                        `transform` outranks the inline transform framer writes,
                        so the mark's entry scale would never render while the
                        two shared an element. */}
                    <span className="hero__mark-float">
                        <motion.img
                            className="hero__mark"
                            src={site.logo}
                            alt={site.name}
                            width="1920"
                            height="1080"
                            {...entry(live, { opacity: 0, scale: 0.82, filter: 'blur(14px)' })}
                            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
                            transition={{ duration: 1.4, ease: EASE }}
                        />
                    </span>

                    <WordReveal className="t-display hero__title" text={title} delay={0.32} />
                </motion.div>

                <motion.div
                    className="hero__plane"
                    style={live ? { opacity: leadOpacity } : undefined}
                >
                    <motion.p
                        className="t-lead hero__lead"
                        {...entry(live, { opacity: 0, y: 22 })}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 0.85, ease: EASE }}
                    >
                        {lead}
                    </motion.p>
                </motion.div>

                <motion.div
                    className="hero__plane"
                    style={live ? { opacity: actionsOpacity } : undefined}
                    inert={actionsGone ? '' : undefined}
                    aria-hidden={actionsGone ? 'true' : undefined}
                >
                    <motion.div
                        className="hero__actions"
                        {...entry(live, { opacity: 0, y: 22 })}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, delay: 1, ease: EASE }}
                    >
                        <Link className="btn btn--accent" to="/services">
                            Explore our work
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </Link>
                        <a className="btn btn--ghost" href={contact.phoneHref}>
                            Call {contact.phone}
                        </a>
                    </motion.div>
                </motion.div>

                <ul
                    className="hero__chips"
                    inert={chipsGone ? '' : undefined}
                    aria-hidden={chipsGone ? 'true' : undefined}
                >
                    {disciplines.map((item, i) => (
                        <Chip
                            key={item.label}
                            item={item}
                            index={i}
                            progress={scrollYProgress}
                            live={live}
                        />
                    ))}
                </ul>

                {/* The handoff. Three accent segments draw in on the first
                    pixels of scroll, then one neutral hairline resolves over
                    them, and the very next horizontal line the reader meets is
                    the trade ticker's own 1px border. The hero doesn't fade to
                    black, it hands off. Nothing is added to the resting
                    composition: the segments only exist as a consequence of
                    scrolling. */}
                <div className="hero__seam" aria-hidden="true">
                    <motion.div
                        className="hero__seam-bars"
                        style={live ? { opacity: barsOpacity } : undefined}
                    >
                        {seam.map((colour, i) => (
                            <SeamBar
                                key={colour}
                                colour={colour}
                                index={i}
                                progress={scrollYProgress}
                                live={live}
                            />
                        ))}
                    </motion.div>

                    <motion.span
                        className="hero__seam-unify"
                        style={live ? { opacity: unifyOpacity } : undefined}
                    />
                </div>
            </motion.div>

            <motion.a
                className="hero__cue"
                href="#statement"
                aria-label="Scroll to continue"
                style={live ? { opacity: cueOpacity } : undefined}
            >
                <span className="hero__cue-track"><span className="hero__cue-dot" /></span>
                <span className="hero__cue-label">Scroll</span>
            </motion.a>
        </section>
    );
}
